/**
 * RewardUIContent Component
 * 
 * Displays the ui_content_json in a human-readable format,
 * breaking down the flat dot notation configuration into organized sections.
 * Works with the new WorkflowContent flat structure.
 */

import React, { useMemo } from 'react';
import { DataList } from '@/components/common/DataList';
import { RichContent } from '@/components/common/RichContent';
import type { WorkflowContent, FormFieldDefinition } from '@/app/types/rewards';
import { getWorkflowTypeLabel } from '@/app/types/rewards';

import './RewardUIContent.scss';

interface RewardUIContentProps {
  content: WorkflowContent | null;
}

// Helper to format field path into readable label
function formatFieldLabel(fieldPath: string): string {
  // Remove the prefix (e.g., 'forms.' or 'components.info_sheet.')
  const parts = fieldPath.split('.');
  const lastPart = parts[parts.length - 1] || fieldPath;
  
  // Convert snake_case to Title Case
  return lastPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper to check if value is a primitive string (not object/array)
function isStringValue(value: unknown): value is string {
  return typeof value === 'string';
}

// Helper to check if a string contains HTML tags
function containsHtml(value: string): boolean {
  return /<[a-z][\s\S]*>/i.test(value);
}

// Helper to check if value is a FormFieldDefinition array
function isFormFieldsArray(value: unknown): value is FormFieldDefinition[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  const first = value[0];
  return typeof first === 'object' && first !== null && 'id' in first && 'type' in first && 'label' in first;
}

// Helper to render form field definitions in a readable format
function renderFormFields(fields: FormFieldDefinition[]): React.ReactNode {
  return (
    <div className="form-fields-display">
      {fields.map((field, idx) => (
        <div key={idx} className="form-field-item">
          <div className="form-field-header">
            <span className="form-field-type">{field.type}</span>
            <code className="form-field-id">{field.id}</code>
            {field.required && <span className="form-field-required">*</span>}
          </div>
          {field.label && (
            <div className="form-field-label">{field.label}</div>
          )}
          {field.options && field.options.length > 0 && (
            <div className="form-field-options">
              {field.options.map((opt, optIdx) => (
                <span key={optIdx} className="option-chip">
                  <code>{opt.value}</code>
                  <span>{opt.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Helper to render complex values as formatted content
function renderValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return '—';
  }
  
  if (typeof value === 'string') {
    return <RichContent content={value} />;
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    
    // Check if it's form fields array
    if (isFormFieldsArray(value)) {
      return renderFormFields(value);
    }
    
    // Check if it's an array of objects (like next_steps)
    if (typeof value[0] === 'object' && value[0] !== null && 'title' in value[0]) {
      return (
        <ol className="next-steps-list">
          {value.map((step: { title: string; description: string }, idx: number) => (
            <li key={idx}>
              <strong>{step.title}</strong>
              <span>{step.description}</span>
            </li>
          ))}
        </ol>
      );
    }
    
    // Simple string array
    return (
      <ol>
        {value.map((item, idx) => (
          <li key={idx}>{String(item)}</li>
        ))}
      </ol>
    );
  }
  
  if (typeof value === 'object') {
    return <pre className="json-preview">{JSON.stringify(value, null, 2)}</pre>;
  }
  
  return String(value);
}

// Group keys by their prefix
interface GroupedFields {
  [prefix: string]: { key: string; value: unknown }[];
}

function groupFieldsByPrefix(content: WorkflowContent): GroupedFields {
  const groups: GroupedFields = {};
  
  for (const [key, value] of Object.entries(content)) {
    if (key === 'workflow_type' || key === '_metadata') continue;
    
    // Use first part for grouping (info_sheet, amount_selection, success)
    const parts = key.split('.');
    const prefix = parts[0] || key;
    
    if (!groups[prefix]) {
      groups[prefix] = [];
    }
    groups[prefix]!.push({ key, value });
  }
  
  // Sort fields within each group according to FIELD_ORDER
  for (const prefix of Object.keys(groups)) {
    const fieldOrder = FIELD_ORDER[prefix];
    if (fieldOrder) {
      groups[prefix]!.sort((a, b) => {
        const aIndex = fieldOrder.indexOf(a.key);
        const bIndex = fieldOrder.indexOf(b.key);
        // Fields not in the order list go to the end
        if (aIndex === -1 && bIndex === -1) return a.key.localeCompare(b.key);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }
  }
  
  return groups;
}

// Map prefix to readable section title
function getSectionTitle(prefix: string): string {
  const titles: Record<string, string> = {
    'info_sheet': 'Info Sheet Page',
    'pre_redemption': 'Pre-Redemption Page',
    'amount_selection': 'Amount Selection Page',
    'success': 'Success Page',
  };
  
  return titles[prefix] || prefix.split('.').map(p => 
    p.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  ).join(' › ');
}

// Preferred section order - matches workflow flow
const SECTION_ORDER = [
  'info_sheet',
  'pre_redemption',
  'amount_selection',
  'success',
];

// Field order within each section - matches UIContentEditor for consistency
const FIELD_ORDER: Record<string, string[]> = {
  'info_sheet': [
    'info_sheet.page_title',
    'info_sheet.illustration',
    'info_sheet.what_is_this_title',
    'info_sheet.what_is_this_content',
    'info_sheet.how_it_works_title',
    'info_sheet.how_it_works_content_base',
    'info_sheet.how_it_works_content_minimum',
    'info_sheet.how_it_works_content_monthly_max',
    'info_sheet.how_it_works_content_yearly_max',
    'info_sheet.how_it_works_content_provider',
    'info_sheet.cta_label',
  ],
  'pre_redemption': [
    'pre_redemption.page_title',
    'pre_redemption.illustration',
    'pre_redemption.description',
    'pre_redemption.form_fields',
    'pre_redemption.cta_label',
    'pre_redemption.post_submit_available',
    'pre_redemption.post_submit_blocking_reason',
    'pre_redemption.info_sheet_description',
    'pre_redemption.success_page_title',
    'pre_redemption.success_title',
    'pre_redemption.success_illustration',
    'pre_redemption.success_description',
    'pre_redemption.success_cta_label',
  ],
  'amount_selection': [
    'amount_selection.page_title',
    'amount_selection.input_label',
    'amount_selection.input_placeholder',
    'amount_selection.help_text_base',
    'amount_selection.help_text_minimum',
    'amount_selection.help_text_monthly_max',
    'amount_selection.help_text_yearly_max',
    'amount_selection.validation_required',
    'amount_selection.validation_min',
    'amount_selection.validation_max_base',
    'amount_selection.validation_max_monthly_limit',
    'amount_selection.validation_max_annual_limit',
    'amount_selection.fixed_amount_title',
    'amount_selection.fixed_amount_label',
    'amount_selection.fixed_amount_value',
    'amount_selection.fixed_remaining_label',
    'amount_selection.fixed_description',
    'amount_selection.form_fields',
    'amount_selection.instructions_title',
    'amount_selection.instructions_content',
    'amount_selection.consent',
  ],
  'success': [
    'success.page_title',
    'success.layout',
    'success.title',
    'success.subtitle',
    'success.main_message',
    'success.illustration_icon',
    'success.card_description',
    'success.cta_text',
    'success.summary_redeemed_label',
    'success.summary_redeemed_value',
    'success.summary_total_points_label',
    'success.summary_total_points_value',
    'success.alert_heading',
    'success.alert_message',
    'success.next_steps',
  ],
};

export const RewardUIContent: React.FC<RewardUIContentProps> = ({ content }) => {
  const groupedFields = useMemo(() => {
    if (!content) return {};
    return groupFieldsByPrefix(content);
  }, [content]);
  
  // Sort sections by preferred order
  const sortedPrefixes = useMemo(() => {
    const prefixes = Object.keys(groupedFields);
    return prefixes.sort((a, b) => {
      const aIndex = SECTION_ORDER.indexOf(a);
      const bIndex = SECTION_ORDER.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [groupedFields]);

  if (!content) {
    return (
      <div className="reward-ui-content empty">
        <p>No UI configuration defined</p>
      </div>
    );
  }

  return (
    <div className="reward-ui-content">
      {/* Workflow Type */}
      {content.workflow_type && (
        <section className="content-section">
          <h4>Workflow Configuration</h4>
          <DataList
            items={[
              { label: 'Workflow Type', value: getWorkflowTypeLabel(content.workflow_type) },
            ]}
          />
        </section>
      )}

      {/* Grouped Content Sections */}
      {sortedPrefixes.map(prefix => {
        const fields = groupedFields[prefix];
        if (!fields || fields.length === 0) return null;
        
        // Helper to check if field is "simple" (short string, no HTML)
        const isSimpleField = (f: { value: unknown }) => 
          isStringValue(f.value) && 
          !String(f.value).includes('\n') && 
          !containsHtml(String(f.value));
        
        return (
          <section key={prefix} className="content-section">
            <h4>{getSectionTitle(prefix)}</h4>
            
            {/* Render all fields in order, with appropriate styling */}
            {fields.map(({ key, value }) => {
              if (isSimpleField({ value })) {
                // Simple field - render as label/value pair
                return (
                  <div key={key} className="simple-field">
                    <span className="field-label">{formatFieldLabel(key)}</span>
                    <span className="field-value">{String(value)}</span>
                  </div>
                );
              }
              
              // Complex field - render with heading and rich content
              return (
                <div key={key} className="complex-field">
                  <h5>{formatFieldLabel(key)}</h5>
                  <div className="field-value">
                    {renderValue(value)}
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}

      {/* Metadata Section */}
      {content._metadata && typeof content._metadata === 'object' && (
        <section className="content-section metadata-section">
          <h4>Content Metadata</h4>
          <DataList
            items={Object.entries(content._metadata as Record<string, unknown>)
              .filter(([, v]) => v !== null && v !== undefined)
              .map(([k, v]) => ({
                label: formatFieldLabel(k),
                value: String(v),
              }))}
          />
        </section>
      )}
    </div>
  );
};

export default RewardUIContent;
